<template>
    <div
        class="default-flag"
        :style="[colorHandler, { transform: `translateY(${this.offsetTop})` }]"
    >
        <p
            v-if="flagNumbered"
            class="number"
            :style="[styleHandler, circleStyles]"
        >
            {{ index + 1 }}
        </p>

        <div
            v-if="showFlagText() && flagText"
            class="flag"
            :class="classHandler"
            :style="[styleHandler, { '--border-radius': borderRadius }]"
        >
            <div class="flag-text" v-html="this.$t(flagText)" />
        </div>
    </div>
</template>

<script>
import { mediaQueriesMixin } from '@/mixins';

export const flagTailTypes = ['default', 'swallowtailed', 'angled', 'rounded'];
export default {
    name: 'DefaultFlag',
    mixins: [mediaQueriesMixin],
    props: {
        flagTail: {
            type: String,
            default: 'default',
        },
        ribboned: {
            type: Boolean,
            default: false,
        },
        offsetTop: {
            type: String,
            default: '-40%',
        },
        fill: {
            type: [String, Boolean],
        },
        borderRadius: {
            type: String,
            default: '10px',
        },
        boldText: {
            type: Boolean,
            default: false,
        },
        textColor: {
            type: String,
            default: '#FFF',
        },
        textShadow: {
            type: Boolean,
            default: false,
        },
        flagShadow: {
            type: Boolean,
            default: false,
        },
        flagText: {
            type: String,
            default: '',
        },
        index: {
            type: Number,
        },
        flagNumbered: {
            type: Boolean,
            default: false,
        },
        firstFlagCircleSize: {
            type: String,
            default: '42px',
        },
        circleSize: {
            type: String,
            default: '32px',
        },
        circleFill: {
            type: String,
            default: '#FF6600',
        },
        circleTextColor: {
            type: String,
            default: '#FFF',
        },
        circleBorderColor: {
            type: String,
            default: '#FF6600',
        },
    },
    methods: {
        showFlagText() {
            const flagPositions =
                this.$globalConstants?.components?.CategoryPage?.flagPosition
                    ?.replace(/ /g, '')
                    ?.split(',')
                    ?.map(Number) || [1, 2];
            return flagPositions.includes(this.index + 1);
        },
    },
    computed: {
        classHandler() {
            return {
                __angled: this.flagTail === 'angled',
                __rounded: this.flagTail === 'rounded',
                __tailed: this.flagTail === 'swallowtailed',
                __ribboned: !!this.ribboned,
            };
        },
        colorHandler() {
            return (
                !!this.fill && {
                    '--primary': this.fill,
                }
            );
        },
        circleStyles() {
            let width;
            let height;
            if (this.index === 0) {
                this.isMobile()
                    ? (width =
                          parseInt(this.firstFlagCircleSize, 10) * 0.75 + 'px')
                    : (width = this.firstFlagCircleSize);
                this.isMobile()
                    ? (height =
                          parseInt(this.firstFlagCircleSize, 10) * 0.75 + 'px')
                    : (height = this.firstFlagCircleSize);
            } else {
                this.isMobile()
                    ? (width = parseInt(this.circleSize, 10) * 0.75 + 'px')
                    : (width = this.circleSize);
                this.isMobile()
                    ? (height = parseInt(this.circleSize, 10) * 0.75 + 'px')
                    : (height = this.circleSize);
            }

            function calculateProportionalFontSize(parentWidth) {
                const numericParentWidth = parseFloat(parentWidth);
                const targetWidth = 42;
                const baseFontSize = 20;
                const ratio = numericParentWidth / targetWidth;
                const proportionalFontSize = baseFontSize * ratio;
                return proportionalFontSize + 'px';
            }

            return {
                width: width,
                height: height,
                background: this.circleFill,
                border: `2px solid ${this.circleBorderColor}`,
                color: this.circleTextColor,
                fontSize: calculateProportionalFontSize(width),
            };
        },
        styleHandler() {
            return {
                color: this.textColor,
                'text-shadow': this.textShadow
                    ? '0 5px 5px rgb(0 0 0 / 25%)'
                    : 'none',
                'font-weight': this.boldText ? 'bold' : 'normal',
                'box-shadow': this.flagShadow
                    ? '2px -2px 14px rgba(0, 0, 0, 0.15)'
                    : 'none',
            };
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/mixins';

.default-flag {
    z-index: 21;
    position: absolute;
    top: 0;
    left: 0;
    color: #fff;
    transform: translateY(-40%);
    max-width: calc(100% - 40px);
    display: flex;
    align-items: center;

    .number {
        position: relative;
        padding: 10px;
        background: var(--primary);
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: -12px;
        z-index: 1;
        font-size: 18px;
        box-shadow: rgba(0, 0, 0, 0.15) 2px -2px 14px !important;
        @include mq('tablet') {
            font-size: 20px;
            margin-left: -20px;
        }
    }

    > .flag {
        position: relative;
        padding: 0 14px 0 16px;
        border-top-left-radius: var(--border-radius);
        background: var(--primary);

        @include mq('tablet') {
            padding: 0 16px;
        }

        &.__tailed::after {
            content: '';
            position: absolute;
            top: 0;
            right: -14px;
            border: 12px solid var(--primary);
            border-right-color: transparent !important;
            @include mq('tablet') {
                right: -18px;
                border: 14px solid var(--primary);
            }
        }

        &.__rounded {
            border-bottom-right-radius: var(--border-radius);
            border-top-right-radius: var(--border-radius);
        }

        &.__angled::after {
            content: '';
            position: absolute;
            top: 0;
            right: -14px;
            border-bottom: 24px solid var(--primary);
            border-right: 14px solid transparent !important;
            @include mq('tablet') {
                border-bottom: 28px solid var(--primary);
            }
        }

        &.__ribboned {
            left: -8px;
            @include mq('tablet') {
                left: -12px;
            }
            &::before {
                content: '';
                position: absolute;
                left: 0;
                bottom: -8px;
                width: 8px;
                height: 8px;
                background: linear-gradient(
                        45deg,
                        rgba(0, 0, 0, 0) 0%,
                        rgba(0, 0, 0, 0) 50%,
                        rgba(0, 0, 0, 0.2) 50%,
                        rgba(0, 0, 0, 0.2) 100%
                    ),
                    linear-gradient(
                        45deg,
                        rgba(0, 0, 0, 0) 0%,
                        rgba(0, 0, 0, 0) 50%,
                        var(--primary) 50%,
                        var(--primary) 100%
                    );
                @include mq('tablet') {
                    bottom: -12px;
                    width: 12px;
                    height: 12px;
                }
            }
        }

        > .flag-text {
            text-overflow: ellipsis;
            white-space: nowrap;
            overflow: hidden;
            font-size: 12px;
            line-height: 24px;
            @include mq('tablet') {
                font-size: 16px;
                line-height: 28px;
            }
        }
    }

    &.flag-eta {
        transform: translateY(-93%) !important;
        left: -1px;

        .flag {
            border-top-right-radius: var(--border-radius);
        }
    }
}
</style>
